/**
 * Studio text for the AI power tools (D10, G04, G17): signing in to MCP services, what they list,
 * the catalog of services, websites as knowledge, what the AI remembers about a customer, the
 * "Test your AI" playground and custom HTTP tools. English is the source language; merged under
 * `aiPower`, apart from the main file so it can grow on its own.
 */
export const aiPowerEn = {
  mcp: {
    preset: {
      label: 'Start from a service',
      other: 'Another service (enter its address)',
      hint: 'Addresses the services publish themselves. Pick one to fill in the name and address.',
      docs: 'The service’s own page about this address',
      verified: 'Address checked against the service’s page on {{date}}.',
      summaries: {
        'intercom-us': 'Look up conversations, contacts and help center articles in Intercom.',
        'intercom-eu': 'Look up conversations, contacts and help center articles in Intercom.',
        gorgias: 'Read tickets and customers in your Gorgias help desk.',
        stripe: 'Look up customers, payments, subscriptions and refunds in Stripe.',
        klaviyo: 'Read customer profiles, lists and campaign results in Klaviyo.',
        calendly: 'Check availability and scheduled events in Calendly.',
        linear: 'Find and read issues and projects in Linear.',
        notion: 'Search and read pages and databases in Notion.',
        atlassian: 'Find and read Jira issues and Confluence pages.',
        asana: 'Find and read tasks and projects in Asana.',
        monday: 'Find and read boards and items in monday.com.',
        sentry: 'Look up errors and issues in Sentry.',
        github: 'Search and read repositories, issues and pull requests on GitHub.',
      },
      notes: {
        'intercom-us': 'Use this one when your Intercom workspace is hosted in the US.',
        'intercom-eu': 'Use this one when your Intercom workspace is hosted in the EU.',
        gorgias: 'Gorgias asks for your account name when you sign in.',
        stripe: 'Sign in with OAuth, or paste a Stripe agent API key as the token.',
        linear: 'Linear also has a read-only address, https://mcp.linear.app/mcp/readonly.',
        asana:
          'If Asana asks for a client ID, create an app in Asana’s developer console and enter it.',
        github: 'Create a personal access token on GitHub and paste it as the token.',
      },
      auth: {
        oauth: 'You sign in with your browser. No token is needed.',
        oauth_or_token: 'You sign in with your browser, or paste a token from the service.',
        token: 'The service gives you a token to paste.',
      },
    },
    auth: {
      none: 'No sign-in or token',
      token: 'Token {{masked}}',
      oauth: 'Signed in through {{host}}',
      needsReconnect: 'The sign-in stopped working. Sign in again.',
      expires: 'Renews itself before {{time}}',
    },
    actions: {
      signIn: 'Sign in',
      signInAgain: 'Sign in again',
      signOut: 'Sign out',
      edit: 'Edit',
      remove: 'Remove',
      catalog: 'Resources and prompts',
      signInLabel: 'Sign in to {{name}}',
      signOutLabel: 'Sign out of {{name}}',
      editLabel: 'Edit {{name}}',
      removeLabel: 'Remove {{name}}',
      catalogLabel: 'See what {{name}} offers besides tools',
    },
    signIn: {
      title: 'Sign in to {{name}}',
      description:
        'You go to the service to sign in and allow Hamnivo, then you come back here. Hamnivo never sees your password.',
      continue: 'Continue to the service',
      manual: 'Enter a client ID',
      manualHint:
        'Only if the service asked for one: some services do not let Hamnivo register itself. Use the redirect address below when you create the client there.',
      redirect: 'Redirect address',
      clientId: 'Client ID',
      clientSecret: 'Client secret',
      clientSecretHint: 'Only if the service gave you one.',
      needsClient:
        'This service does not let Hamnivo register itself. Enter the client ID the service gave you.',
      leaving: 'Going to the service…',
      signedOut: 'Signed out of {{name}}.',
    },
    signOut: {
      title: 'Sign out of {{name}}?',
      body: 'Hamnivo tells the service to forget its access, and deletes the saved sign-in. The tools stay, but they cannot run until you sign in again or add a token.',
      confirm: 'Sign out',
    },
    callback: {
      title: 'Signing in',
      working: 'Finishing the sign-in…',
      done: 'Signed in to {{name}}.',
      synced: 'Its tools are listed under Tools: look at each one before turning it on.',
      syncFailed: 'Signed in, but the tools could not be listed yet. Check the service again.',
      refused: 'The service did not allow the sign-in.',
      failed: 'The sign-in did not finish.',
      ended: 'This sign-in has ended or was started by someone else. Start it again from Tools.',
      missing: 'This page is for finishing a sign-in. Start one from Tools.',
      back: 'Back to Tools',
    },
    edit: {
      title: 'Edit {{name}}',
      description: 'Change its name, its address or its token.',
      label: 'Name',
      url: 'Address',
      token: 'New token',
      tokenHint:
        'Leave this empty to keep what is saved. A new token replaces a sign-in. It is stored encrypted and never shown again.',
      removeToken: 'Remove the saved token',
      removeSignIn: 'To remove a sign-in, use Sign out.',
      urlWarning:
        'If the address changes, the tools go back to review and what the service lists is read again. A saved token stays only when the new address is on the same site, and a sign-in ends.',
      save: 'Save changes',
      saved: '{{name}} was updated.',
      nothing: 'Nothing was changed.',
    },
    remove: {
      title: 'Remove {{name}}?',
      body: 'Its tools are turned off and leave the list, requests waiting on them are cancelled, and its token or sign-in is deleted. What they did stays in the log. You can connect the same address again later.',
      confirm: 'Remove',
      done: '{{name}} was removed.',
    },
    catalog: {
      title: 'What {{name}} offers',
      description:
        'The resources and prompts the service listed at the last check, besides tools. The words in quotes come from the service.',
      checked: 'Listed {{time}}',
      neverChecked: 'Check the service first to see what it offers.',
      resources: 'Resources',
      resourcesNote:
        'Documents and data the service offers. Agents with the “Read a resource” tool can read the ones you switch on, and no others.',
      noResources: 'The service lists no resources.',
      readable: 'Agents may read this',
      readableLabel: 'Let agents read {{name}}',
      readableHint: 'Only with the “Read a resource” tool.',
      type: 'Type',
      size: 'Size',
      uri: 'Address',
      fromService: 'From the service',
      prompts: 'Prompts',
      promptsNote:
        'Ready-made requests the service offers. They are listed for you to see; agents do not use them.',
      noPrompts: 'The service lists no prompts.',
      arguments: 'Asks for',
      required: 'required',
      needTool:
        'Agents read resources with the “Read a resource” tool. Add it under Tools, then give it to an agent.',
      saved: 'Saved.',
      loading: 'Loading what the service offers',
    },
  },
  web: {
    badge: 'Website',
    add: 'Add website',
    line: {
      reading: 'Reading now',
      waiting: 'Waiting to be read',
      never: 'Not read yet',
      last: 'Read {{time}}',
    },
    dialog: {
      addTitle: 'Add a website',
      addDescription:
        'Hamnivo reads the pages of a website and keeps them up to date, so the AI can answer from them. It follows the site’s robots.txt, reads slowly and stays on the site you give.',
      editTitle: 'Website settings',
      brand: 'Brand',
      url: 'Website address',
      urlHint:
        'The page to start from, such as https://example.com/help/. Only pages in that folder are read.',
      urlRequired: 'Enter the address of the website.',
      urlInvalid: 'Enter a full address that starts with https://.',
      name: 'Name',
      nameHint: 'Shown in the list of sources. The site’s name is used when this is empty.',
      mode: 'What to read',
      modeSite: 'The whole site',
      modeSiteHint:
        'Its sitemap and the links between its pages, inside the folder of the address.',
      modePage: 'Just this page',
      modePageHint: 'Only the address you entered.',
      maxPages: 'Most pages to read',
      maxPagesHint: 'From 1 to 500.',
      maxPagesInvalid: 'Enter a whole number from 1 to 500.',
      maxDepth: 'How many links to follow',
      maxDepthHint: 'Counted from the first page. 0 reads the first page and the sitemap only.',
      maxDepthInvalid: 'Enter a whole number from 0 to 5.',
      schedule: 'Read again',
      visibility: 'Who can get answers from it',
      submit: 'Add website',
      save: 'Save changes',
      added: '{{name}} was added. Hamnivo starts reading it now.',
      saved: 'The website’s settings were saved.',
      later: 'The first read can take a few minutes. You can leave this page.',
    },
    schedule: { daily: 'Every day', weekly: 'Every week', manual: 'Only when I ask' },
    panel: {
      title: 'Website',
      address: 'Address',
      reads: 'What it reads',
      readsSite: 'The whole site: up to {{pages}} pages, {{depth}} links deep',
      readsPage: 'This page only',
      schedule: 'Read again',
      next: 'Next read {{time}}',
      pages: 'Pages kept',
      lastRead: 'Last read',
      never: 'This website has not been read yet.',
      state: {
        queued: 'Waiting to be read',
        running: 'Reading now',
        succeeded: 'Read',
        partial: 'Read, with problems',
        failed: 'Could not be read',
        never: 'Not read yet',
      },
      polling: 'Studio checks for news every few seconds while the website is read.',
      summary: {
        title: 'What the last read did',
        added: 'New: {{total}}',
        updated: 'Changed: {{total}}',
        unchanged: 'Unchanged: {{total}}',
        retired: 'Removed: {{total}}',
        sitemaps: 'Sitemaps read: {{total}}',
        leftOut: 'Left out',
        skipped: {
          robots: 'The site asks readers to stay away (robots.txt): {{total}}',
          offsite: 'On another site: {{total}}',
          noindex: 'Pages that ask not to be indexed: {{total}}',
          type: 'Not a web page or a PDF: {{total}}',
          duplicate: 'Copies of a page already read: {{total}}',
          empty: 'No readable text: {{total}}',
        },
        problems: 'Could not be read',
        truncated:
          'The read stopped before it reached every page, because of the page limit or the time. Raise the limit in Settings to read more.',
        robots: {
          read: 'The site’s robots.txt was read and followed.',
          missing: 'The site has no robots.txt, so every page was allowed.',
          unreadable: 'The site’s robots.txt could not be read, so nothing was read.',
        },
      },
      actions: {
        sync: 'Read now',
        syncLabel: 'Read {{name}} now',
        queued: 'Hamnivo will read {{name}} in a moment.',
        settings: 'Settings',
        settingsLabel: 'Settings for {{name}}',
        showPages: 'See pages',
        hidePages: 'Hide pages',
      },
      pagesList: {
        title: 'Pages',
        loading: 'Loading pages',
        empty: 'No pages yet.',
        columns: { address: 'Address', state: 'State', note: 'Note' },
        states: { ok: 'Read', failed: 'Failed', gone: 'Removed' },
      },
    },
  },
  http: {
    title: 'Custom tools',
    description:
      'Connect a web service of your own. Import its OpenAPI description, or write one request by hand. Each request becomes a tool that waits for your review before any agent can use it.',
    importLabel: 'Import a description',
    writeLabel: 'Write a request',
    loading: 'Loading custom tools',
    empty: 'No custom tools yet.',
    source: {
      kinds: { openapi: 'Imported description', manual: 'Written by hand' },
      status: { active: 'On', disabled: 'Off' },
      toolStatus: { review: 'Waiting for review', enabled: 'On', disabled: 'Off' },
      tools: 'Tools: {{total}}',
      review: 'Waiting for review: {{total}}',
      auth: {
        none: 'No credential',
        bearer: 'Token {{masked}}',
        header: 'Secret in the {{header}} header {{masked}}',
      },
      spec: 'From the description “{{title}}” {{version}}',
      requests: 'Requests of {{name}}',
      open: 'Open',
      openLabel: 'Open the tool {{tool}}',
      editRequest: 'Edit',
      editRequestLabel: 'Edit the request {{tool}}',
      imported: 'Imported: import it again to change it.',
      addRequest: 'Add a request',
      importMore: 'Import more',
      edit: 'Edit',
      editLabel: 'Edit {{name}}',
      turnOff: 'Turn off',
      turnOn: 'Turn on',
      remove: 'Remove',
      removeLabel: 'Remove {{name}}',
      off: 'This service is off, so its tools cannot run.',
    },
    service: {
      name: 'Name of the service',
      nameHint: 'How you find it in the list, for example “Order system”.',
      baseUrl: 'Address of the service',
      baseUrlHint:
        'Every request goes below this address, for example https://api.example.com/v1. It starts with https:// and has no ? or #.',
      auth: {
        legend: 'How requests sign in',
        none: 'No credential',
        noneHint: 'For a service that needs none.',
        bearer: 'A token',
        bearerHint: 'Sent as “Authorization: Bearer …”.',
        header: 'A secret in a header',
        headerHint: 'Sent in the header you name.',
        token: 'Token',
        tokenHint: 'Stored encrypted. Only its last characters are ever shown.',
        headerName: 'Header name',
        headerNameHint: 'For example X-Api-Key.',
        value: 'Secret value',
        valueHint: 'Stored encrypted. Only its last characters are ever shown.',
      },
      errors: {
        name: 'Give the service a name.',
        baseUrl: 'Enter the address, starting with https://.',
        token: 'The token has at least 8 characters.',
        headerName: 'Use letters, digits, hyphens and underscores.',
        value: 'Enter the secret value.',
      },
    },
    importDialog: {
      title: 'Import an OpenAPI description',
      titleMore: 'Import more tools into {{name}}',
      description:
        'Hamnivo reads an OpenAPI 3 description, as JSON or YAML, and lists the requests it can turn into tools. Nothing is saved until you add them.',
      where: 'Where is the description?',
      paste: 'I have the text or a file',
      address: 'It is at a web address',
      text: 'The description',
      textHint: 'Paste the JSON or YAML here, or choose a file. Up to 900,000 characters.',
      file: 'Choose a file',
      fileTooBig:
        'This file is too large. Leave out the requests you do not need, or write them by hand.',
      specUrl: 'Address of the description',
      specUrlHint:
        'For example https://api.example.com/openapi.json. Hamnivo reads it from its own server, under the same rules as every address.',
      read: 'Read the description',
      needText: 'Paste the description or choose a file first.',
      needUrl: 'Enter the address of the description first.',
      found: '“{{title}}” {{version}}: {{count}} requests can become tools.',
      foundUntitled: '{{count}} requests can become tools.',
      none: 'No request in this description can become a tool.',
      readAgain: 'Read a different description',
      servers: 'The description says the service is at',
      serversHint: 'This is text from the description. Check the address before you use it.',
      authUnsupported:
        'The description asks for a kind of sign-in ({{kind}}) that tools cannot do for you. If the service also takes a token or a key in a header, choose that below.',
      operations: 'Requests to turn into tools',
      operationsHint:
        'Each becomes a tool that waits for your review. The ones that only read are chosen for you. Requests that change something are yours to choose, and a person approves each use.',
      selectReads: 'Choose the ones that only read',
      selectAll: 'Choose all',
      selectNone: 'Choose none',
      chosen: '{{count}} chosen',
      already: 'Already a tool of this service',
      left: 'Left out of it:',
      skipped: 'Requests that cannot become tools ({{count}})',
      submit: 'Add the chosen tools',
      needOne: 'Choose at least one request.',
      added: '{{count}} tools added. They wait for your review.',
    },
    endpointDialog: {
      addTitle: 'Write a request',
      addTitleTo: 'Add a request to {{name}}',
      editTitle: 'Edit {{tool}}',
      description:
        'One request your service understands. The AI is told what it does and which details to fill in.',
      editWarning:
        'Saving sends this tool back to review and ends requests that wait for approval.',
      title: 'Name of the tool',
      titleHint: 'Shown to your team, for example “Look up an order”.',
      about: 'What the AI is told it does',
      aboutHint: 'Say when to use it and what it gives back. Write it for the AI.',
      method: 'Method',
      path: 'Path',
      pathHint: 'Starts with /. Write {orderId} where a value goes, for example /orders/{orderId}.',
      risk: 'What it can do',
      riskDefault: 'Decide from the method',
      details: 'Details the AI fills in',
      detailsHint: 'Each is one value the AI gives with the request.',
      noDetails: 'No details. The request is sent as it is.',
      addDetail: 'Add a detail',
      detailLabel: 'Detail {{n}}',
      removeDetail: 'Remove detail {{n}}',
      missing: 'The path has {{names}}, but no detail fills it in.',
      fill: 'Add {{name}}',
      detail: {
        name: 'Name',
        nameHint: 'What the AI calls it: letters, digits and underscores.',
        place: 'Goes in',
        places: {
          path: 'The path',
          query: 'The query',
          header: 'A header',
          body: 'The JSON body',
        },
        key: 'Name in the request',
        keyHint: 'Only when it differs from the name.',
        type: 'Kind of value',
        types: {
          string: 'Text',
          integer: 'Whole number',
          number: 'Number',
          boolean: 'Yes or no',
        },
        required: 'Always needed',
        requiredPath: 'Always needed, because it is in the path',
        about: 'What it is',
      },
      errors: {
        title: 'Give the tool a name.',
        about: 'Say what the tool does.',
        path: 'Start the path with /.',
        detailName: 'Use letters, digits and underscores, starting with a letter.',
        detailDuplicate: 'Two details have the same name.',
      },
      submitAdd: 'Add the tool',
      submitEdit: 'Save',
      added: '{{name}} added. It waits for your review.',
      saved: '{{name}} saved. It is back in review.',
    },
    editDialog: {
      title: 'Edit {{name}}',
      description: 'Change the name or the address, or replace the credential.',
      name: 'Name of the service',
      baseUrl: 'Address of the service',
      baseUrlWarning:
        'A new address sends the tools back to review. A credential never follows to another site.',
      credential: 'Credential',
      keep: 'Keep it',
      replace: 'Replace it',
      remove: 'Remove it',
      save: 'Save',
      saved: '{{name}} saved.',
      nothing: 'Nothing changed.',
    },
    removeDialog: {
      title: 'Remove {{name}}?',
      body: 'Its tools are turned off and leave the list, requests waiting on them are cancelled, and its credential is deleted. What the tools did stays in the action log.',
      confirm: 'Remove',
      done: '{{name}} removed.',
    },
    tool: {
      service: 'Custom service',
      request: 'Request',
      titleLabel: 'Name of the tool',
      descriptionLabel: 'What the AI is told it does',
      descriptionHint:
        'The AI reads this instead of the words in the description. Check it before you turn the tool on.',
      errors: { title: 'Give the tool a name.', description: 'Say what the tool does.' },
    },
  },
  memoryCard: {
    title: 'What the AI remembers',
    open: 'Open memory',
    none: 'Nothing is remembered here: this visitor is not a known customer yet.',
    off: 'This brand’s AI does not use memory, so nothing here reaches its replies.',
    empty: 'Nothing is remembered about this customer yet.',
    note: 'The AI uses only current values, and only if the customer agreed to be remembered.',
    thisChat: 'this conversation',
    pendingNote: 'Not used until someone confirms it.',
    review: 'Review',
    reviewLabel: 'Review {{label}}',
    correct: 'Correct',
    correctLabel: 'Correct {{label}}',
    delete: 'Delete',
    deleteLabel: 'Delete {{label}}',
    correctTitle: 'Correct “{{label}}”',
    correctBody: 'The new value replaces this one. The old one stays in the history.',
  },
  playground: {
    title: 'Test your AI',
    description:
      'Talk to this agent the way a customer would, with the model it really uses. It answers from its instructions and its knowledge. Nothing is saved, nothing reaches a customer or the inbox, and no tool is run. Each message uses tokens, like a real reply.',
    testOnly: 'Test only: nothing is sent or saved',
    version: 'Version to try',
    versions: {
      draft: 'Saved draft (version {{version}})',
      published: 'Live version ({{version}})',
    },
    unsaved:
      'Some changes are not saved. The test uses the saved draft, so save first to try them.',
    noVersion: 'Save a draft to try this agent.',
    startOver: 'Start over',
    you: 'You, as the customer',
    ai: 'The AI',
    message: 'Your message',
    messageHint: 'Write what a customer might write.',
    send: 'Send',
    thinking: 'The AI is answering…',
    empty: 'Write a message to start. Try a question your customers ask often.',
    tooLong: 'This test conversation is long. Start over to keep trying.',
    outcome: {
      customer_request:
        'The customer asked for a person, so a live chat would go to your team here.',
      assist_mode:
        'This agent only suggests replies to your team, so a live chat would go to them.',
      ai_blocked:
        'The AI could not write a reply that follows its rules, so a live chat would go to your team.',
      other: 'A live chat would go to your team here.',
    },
    errors: {
      auth: 'The model provider refused the key or the account. Check the model connection.',
      rate_limited: 'The model provider is limiting requests. Try again in a moment.',
      overloaded: 'The model provider is busy. Try again in a moment.',
      unavailable: 'The model provider is not available right now.',
      timeout: 'The model took too long to answer.',
      aborted: 'The request was stopped.',
      invalid_request:
        'The model provider did not accept the request. Check the model name and settings.',
      content_filtered: 'The model provider’s filter stopped the answer.',
      context_length: 'The conversation is too long for the model. Start over.',
      no_credentials: 'There is no key for this model. Add a model connection first.',
      connection_disabled: 'The model connection is off. Choose another connection.',
      provider_mismatch: 'The model connection is for a different provider than the one chosen.',
      model_not_allowed: 'This model is not allowed on the connection.',
      provider_not_supported: 'This kind of model is not supported.',
      empty_reply: 'The model answered with nothing.',
      unknown: 'The model did not answer.',
    },
    sources: 'Sources it used',
    actions: 'Tools it asked for',
    actionsNote: 'Tools are never run here. This shows what a live chat would do.',
    decisions: {
      would_run: 'Would run now',
      needs_approval: 'Would ask for approval',
      denied: 'Not allowed',
      invalid: 'Wrong details',
    },
    details: 'Details it sent',
    steps: 'Steps ({{total}})',
    stepsLabel: 'What the AI did for this answer',
    stats: 'Model {{model}} · tokens {{input}} in, {{output}} out · {{time}}',
    statsNoModel: 'No model was called · {{time}}',
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
/** Every locale has exactly the English keys; TypeScript reports a missing one. */
export type AiPowerTranslation = Strings<typeof aiPowerEn>;
